/** @jsxImportSource @emotion/react */

import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import { Radio } from '@mui/material'
import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Modal from '@mui/material/Modal'
import Typography from '@mui/material/Typography'
import { isEqual, sortBy } from 'lodash'
import { SyntheticEvent, useCallback, useState } from 'react'
import { ReactSortable } from 'react-sortablejs'
import { QuestionEdit } from 'pagesNew/Questions'
import { SORTABLE_LIST_ANIMATION } from 'constants/consts'
import { getAnswerLetterLabel } from 'utils/getAnswerLetterLabel'
import { findAllQuestion_findAllQuestion_items as Question } from '__generatedTypes__/findAllQuestion'
import { QuestionTypeEnum } from '__generatedTypes__/globalTypes'
import { parseRxWritingAnswerHtml } from 'pagesNew/Questions/Form/RxWritingAnswers/helpers'
import { parseNumericResponseAnswerHtml } from 'pagesNew/Questions/Form/NumericResponseAnswer/helpers'
import {
  parseTextCompletionAnswerHtml,
  TEXT_COMPLETION_BLANK_LABELS,
} from 'pagesNew/Questions/Form/TextCompletionAnswers/helpers'
import { isPassageQuestion, isPlainTextOptions } from 'pagesNew/Questions/Form/questionTypeConfig'
import { colors } from 'theme/colors'
import { ReactComponent as EmptyListSvg } from './images/empty.svg'
import * as classes from './styles'
import { AccordionSummaryContent, QuestionPassageBadge } from './components/AccordionSummaryContent'

export type QuestionPassageRange = {
  startQuestion?: number
  endQuestion?: number
}

const getPassageCoveringQuestion = (questionNumber: number, passages: QuestionPassageRange[]) =>
  passages.findIndex((passage) => {
    const start = Number(passage.startQuestion)
    const end = Number(passage.endQuestion)

    return Number.isFinite(start) && Number.isFinite(end) && questionNumber >= start && questionNumber <= end
  })

export const getQuestionPassageBadge = (
  question: Pick<Question, 'type'>,
  questionNumber: number,
  passages?: QuestionPassageRange[]
): QuestionPassageBadge | null => {
  if (!passages) {
    return null
  }

  const passageIndex = getPassageCoveringQuestion(questionNumber, passages)
  const hasPassage = passageIndex >= 0
  const isPassageType = isPassageQuestion(question.type)

  if (isPassageType && !hasPassage) {
    return { text: 'No passage', color: colors.red }
  }

  if (hasPassage) {
    return {
      text: `Passage #${passageIndex + 1}`,
      color: isPassageType ? colors.green : colors.red,
    }
  }

  return null
}

export const getAnswerTextPreview = (questionType: QuestionTypeEnum, textAsHtml: string) => {
  if (questionType === QuestionTypeEnum.RxWritingNDEB) {
    const answerData = parseRxWritingAnswerHtml(textAsHtml)

    return (
      `Drug: ${answerData.drug} / strength: ${answerData.strength.join(', ')} / disp: ${answerData.disp.join(', ')} ` +
      `/ sig: ${answerData.sig.join(', ')} / repeat: ${answerData.repeat}`
    )
  }

  if (questionType === QuestionTypeEnum.NumericResponse) {
    const answerData = parseNumericResponseAnswerHtml(textAsHtml)

    return answerData.isFraction
      ? `${answerData.numeratorValue} / ${answerData.denominatorValue}`
      : String(answerData.value ?? '')
  }

  return textAsHtml
}

const renderTextCompletionPreview = (textAsHtml: string, blankIndex: number) => {
  const { options } = parseTextCompletionAnswerHtml(textAsHtml)
  const blankLabel = TEXT_COMPLETION_BLANK_LABELS[blankIndex] ?? `(${blankIndex + 1})`

  return (
    <>
      Blank {blankLabel}:{' '}
      {options.map((option, optionIndex) => (
        <span key={option.id || optionIndex}>
          {optionIndex > 0 && ' / '}
          {option.isCorrect ? <strong>{option.value}</strong> : option.value}
        </span>
      ))}
    </>
  )
}

export type QuestionAccordionProps = {
  questions: Question[]
  onSort?: (questions: Question[]) => void
  onDeleteClick?: (question: Question, index: number) => void
  onQuestionUpdated?: (questionUpd: Question, index: number) => void
  passages?: QuestionPassageRange[]
  getSectionBadge?: (questionNumber: number) => QuestionPassageBadge | null
}

export function QuestionAccordion({
  questions,
  onSort,
  onDeleteClick,
  onQuestionUpdated,
  passages,
  getSectionBadge,
}: QuestionAccordionProps) {
  const sortedQuestions = questions.map((question) => ({ id: question.id, question }))
  const [expanded, setExpanded] = useState<string | null>(questions[0]?.id || null)
  const [selectedQuestion, setSelectedQuestion] = useState<{ id: string; index: number } | null>(null)

  const handleSort = useCallback(
    (sortedQuestionsNew: Array<{ id: string; question: Question }>) => {
      const newQuestions = sortedQuestionsNew.map(({ question }) => question)

      if (isEqual(questions, newQuestions)) {
        return
      }

      onSort?.(newQuestions)
    },
    [questions]
  )

  const handleDeleteClick = (e: SyntheticEvent<SVGSVGElement>, question: Question, index: number) => {
    e.stopPropagation()
    onDeleteClick?.(question, index)
  }

  const handleEditClick = (e: SyntheticEvent<SVGSVGElement>, question: Question, index: number) => {
    e.stopPropagation()
    setSelectedQuestion({ id: question.id, index })
  }

  const handleExpandClick = (Question: string) => () => {
    setExpanded((prevState) => (prevState === Question ? null : Question))
  }

  if (!questions.length) {
    return (
      <div css={[classes.container, classes.emptyContainer]}>
        <EmptyListSvg />
        <div>No questions at the moment</div>
      </div>
    )
  }

  return (
    <>
      <ReactSortable
        list={sortedQuestions}
        setList={handleSort}
        animation={SORTABLE_LIST_ANIMATION}
        css={classes.container}
        data-testid="questions-list"
      >
        {sortedQuestions.map(({ question }, index) => (
          <Accordion key={question.id} expanded={expanded === question.id} onChange={handleExpandClick(question.id)}>
            <AccordionSummary
              css={[classes.accordionSummary, expanded === question.id && classes.accordionSummaryOpen]}
            >
              <AccordionSummaryContent
                index={index}
                expanded={expanded}
                question={question}
                sectionBadge={getSectionBadge?.(index + 1)}
                passageBadge={getQuestionPassageBadge(question, index + 1, passages)}
                onSuccessfulEdit={onQuestionUpdated}
                onEditClick={handleEditClick}
                onDeleteClick={handleDeleteClick}
              />
            </AccordionSummary>
            <AccordionDetails css={classes.accordionDetails}>
              <div css={classes.questionTitle}>
                <Typography variant="body2">Title</Typography>
                <div
                  css={classes.itemContent}
                  dangerouslySetInnerHTML={{ __html: question.questionAsHtml }}
                  data-testid="question-text"
                />
              </div>
              {sortBy(question.answer ?? [], 'order').map((answer, index) => {
                const isRxWriting = question.type === QuestionTypeEnum.RxWritingNDEB
                const isNumericResponse = question.type === QuestionTypeEnum.NumericResponse
                const isTextCompletion = question.type === QuestionTypeEnum.TextCompletion
                const showOnlyAnswerTextPreview = isRxWriting || isNumericResponse || isTextCompletion

                return (
                  <div key={answer.id} css={classes.block} data-testid={`question-answer-${index}`}>
                    <div css={classes.answerTitle}>
                      {!showOnlyAnswerTextPreview && (
                        <Typography variant="body3" css={classes.answerLabel}>
                          {getAnswerLetterLabel(index)}.
                        </Typography>
                      )}
                      {isTextCompletion ? (
                        <div css={classes.itemContent} data-testid={`question-answer-${index}-text`}>
                          {renderTextCompletionPreview(answer.textAsHtml, index)}
                        </div>
                      ) : isPlainTextOptions(question.type) ? (
                        <div css={classes.itemContent} data-testid={`question-answer-${index}-text`}>
                          {getAnswerTextPreview(question.type, answer.textAsHtml)}
                        </div>
                      ) : (
                        <div
                          css={classes.itemContent}
                          dangerouslySetInnerHTML={{ __html: getAnswerTextPreview(question.type, answer.textAsHtml) }}
                          data-testid={`question-answer-${index}-text`}
                        />
                      )}
                    </div>
                    {answer.isCorrect && !showOnlyAnswerTextPreview && (
                      <div css={classes.checked}>
                        <Radio checked />
                        <Typography css={classes.checkedTitle} variant="body2">
                          Correct
                        </Typography>
                      </div>
                    )}
                  </div>
                )
              })}
            </AccordionDetails>
          </Accordion>
        ))}
      </ReactSortable>
      {/* added disableEnforceFocus sx={{ zIndex: 100 }} to fix an issue with editor tooltips */}
      <Modal
        open={!!selectedQuestion}
        onClose={() => setSelectedQuestion(null)}
        disableEnforceFocus
        sx={{ zIndex: 100 }}
      >
        <div css={classes.modal}>
          <div css={classes.modalContainer}>
            <CloseRoundedIcon css={classes.iconClose} onClick={() => setSelectedQuestion(null)} />
            <QuestionEdit
              questionEditId={selectedQuestion?.id ?? ''}
              onSubmit={(question?: Question) => {
                if (question && selectedQuestion) {
                  onQuestionUpdated?.(question, selectedQuestion.index)
                }
                setSelectedQuestion(null)
              }}
              disableDeleteButton
            />
          </div>
        </div>
      </Modal>
    </>
  )
}

export default QuestionAccordion
